<template>
  <v-navigation-drawer
    :value="value"
    right
    stateless
    fixed
    width="380"
    class="side-quests"
    :style="cssVars"
  >

    <div class="sq-header">
      <div class="sq-title">Side Quests</div>

      <button class="sq-close-btn" @click="closeDrawer">
        <i class="fa-regular fa-xmark"></i>
      </button>
    </div>

    <div class="sq-divider"></div>

    <!-- Tabs -->
    <div class="sq-tabs-container">
      <div class="sq-tabs">
      <button
        class="sq-tab"
        :class="{ active: activeTab === 'daily' }"
        @click="activeTab = 'daily'"
      >
        <span class="sq-tab-icon">
          <i class="fa-solid fa-bullseye"></i>
        </span>
        Daily Quest
      </button>

      <button
        class="sq-tab"
        :class="{ active: activeTab === 'weekly' }"
        @click="activeTab = 'weekly'"
      >
        <span class="sq-tab-icon">
          <i class="fa-solid fa-calendar"></i>
        </span>
        Weekly Quest
      </button>
      </div>
    </div>

    <!-- List -->
    <div class="sq-list">
      <div
        v-for="q in filteredQuests"
        :key="q.id"
        class="quest-card"
        :class="{
          active: q.status === 'active',
          disabled: q.status === 'disabled',
        }"
      >
        <div class="quest-left">
          <div class="quest-bullets" v-if="q.bullets && q.bullets > 0">
            <span
              v-for="n in q.bullets"
              :key="n"
              class="bullet"
              :class="{ completed: n <= q.completedBullets }"
            >
              <i class="fa-solid fa-check"></i>
            </span>
          </div>

          <div class="quest-title-row">
            <span class="quest-icon">
              <i class="fa-solid fa-fire"></i>
            </span>
            <span class="quest-title">{{ q.title }}</span>
          </div>

          <div class="quest-subtitle">
            {{ q.subtitle }}
          </div>
        </div>

        <div class="quest-right">
          <div class="quest-reward-icon">
            <i class="fa-solid fa-gift"></i>
          </div>
          <div class="quest-reward-label">Reward:</div>
          <div class="quest-reward-value">{{ q.reward }}</div>
        </div>
      </div>
    </div>
  </v-navigation-drawer>
</template>

<script>
export default {
  name: "SideQuestsDrawer",
  props: {
    value: { type: Boolean, default: false },
    quests: { type: Array, default: () => [] },
    primaryColor: { type: String, default: "EF4444" },
  },
  data() {
    return {
      activeTab: "weekly",
    };
  },
  computed: {
    filteredQuests() {
      return (this.quests || []).filter((q) => q.period === this.activeTab);
    },
    cssVars() {
      const color = this.primaryColor.replace('#', '');
      const r = parseInt(color.substr(0, 2), 16);
      const g = parseInt(color.substr(2, 2), 16);
      const b = parseInt(color.substr(4, 2), 16);
      return {
        '--sq-primary-color': `#${color}`,
        '--sq-primary-rgb': `${r}, ${g}, ${b}`,
      };
    },
  },
  methods: {
    closeDrawer() {
      this.$emit("input", false);
    },
  },
};
</script>

<style scoped lang="scss">
.side-quests {
  background: #ffffff;
}

.sq-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 16px 10px;
}

.sq-title {
  font-family: "Avenir", sans-serif;
  font-size: 18px;
  font-weight: 800;
  color: #1f1f1f;
}

.sq-close-btn {
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  color: #6b7280;
  font-size: 16px;

  &:hover {
    background: #f3f4f6;
  }
}

.sq-divider {
  height: 1px;
  background: #ececec;
}

/* Tabs */
.sq-tabs-container {
  display: flex;
  justify-content: center;
  padding: 14px 16px 10px;
}

.sq-tabs {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: #111827;
  padding: 4px;
  border-radius: 24px;
}

.sq-tab {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 36px;
  padding: 0 16px;
  border: none;
  border-radius: 20px;
  background: transparent;
  color: #ffffff;
  font-family: "Avenir", sans-serif;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  user-select: none;
  transition: background 0.2s;

  &:not(.active):hover {
    background: rgba(255, 255, 255, 0.1);
  }

  &.active {
    background: var(--sq-primary-color);
    color: #ffffff;

    .sq-tab-icon {
      background: #ffffff;

      i {
        color: var(--sq-primary-color);
      }
    }
  }
}

.sq-tab-icon {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;

  i {
    font-size: 12px;
    color: #ffffff;
  }
}

/* Quest List */
.sq-list {
  padding: 12px 16px 18px;
  overflow-y: auto;
  max-height: calc(100vh - 160px);
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.quest-card {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  border: 1px solid #e5e5e5;
  border-radius: 12px;
  background: #ffffff;
  padding: 14px 16px;

  &.active {
    border-color: var(--sq-primary-color);
  }

  &.disabled {
    opacity: 0.5;
  }
}

.quest-left {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.quest-bullets {
  display: flex;
  gap: 6px;
  margin-bottom: 8px;
}

.bullet {
  width: 20px;
  height: 20px;
  border-radius: 80%;
  border: 2px solid var(--sq-primary-color);
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;

  i {
    font-size: 10px;
    color: transparent;
  }

  &.completed {
    i {
      color: var(--sq-primary-color);
    }
  }
}

.quest-title-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.quest-icon {
  width: 24px;
  height: 24px;
  border-radius: 6px;
  background: rgba(var(--sq-primary-rgb), 0.1);
  display: flex;
  align-items: center;
  justify-content: center;

  i {
    color: var(--sq-primary-color);
    font-size: 12px;
  }
}

.quest-title {
  font-family: "Avenir", sans-serif;
  font-size: 14px;
  font-weight: 800;
  color: #111827;
}

.quest-subtitle {
  font-family: "Avenir", sans-serif;
  font-size: 11px;
  color: #9ca3af;
  margin-left: 32px;
}

.quest-right {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: 2px;
}

.quest-reward-icon {
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;

  i {
    font-size: 20px;
    color: var(--sq-primary-color);
  }
}

.quest-reward-label {
  font-family: "Avenir", sans-serif;
  font-size: 11px;
  color: var(--sq-primary-color);
}

.quest-reward-value {
  font-family: "Avenir", sans-serif;
  font-size: 14px;
  font-weight: 800;
  color: var(--sq-primary-color);
}

/* Dark Mode Styles */
.theme--dark {
  .side-quests {
    background: var(--v-cardBackground-base) !important;
  }

  .sq-title {
    color: var(--v-fullWhite-base) !important;
  }

  .sq-close-btn {
    color: #9ca3af;

    &:hover {
      background: rgba(255, 255, 255, 0.1);
    }
  }

  .sq-divider {
    background: #424242;
  }

  .sq-tabs {
    background: #2a2f3a;
  }

  .sq-tab {
    color: rgba(255, 255, 255, 0.8);

    &:not(.active):hover {
      background: rgba(255, 255, 255, 0.05);
    }

    &.active {
      background: var(--sq-primary-color);
      color: #ffffff;
    }
  }

  .quest-card {
    background: #2a2f3a;
    border-color: #424242;

    &.active {
      border-color: var(--sq-primary-color);
    }

    &.disabled {
      opacity: 0.5;
    }
  }

  .quest-icon {
    background: rgba(var(--sq-primary-rgb), 0.2);
  }

  .quest-title {
    color: var(--v-fullWhite-base) !important;
  }

  .quest-subtitle {
    color: rgba(255, 255, 255, 0.6);
  }

  .quest-reward-label {
    color: rgba(255, 255, 255, 0.6);
  }
}
</style>
